import { apiDelete, apiGet, apiPatch, apiPost } from '../client';
import {
  TabListResponseSchema,
  TabResponseSchema,
  TabSettlementResponseSchema,
  type ChargePaymentMethod,
  type TabRecord,
  type TabSettlement,
} from '../schemas';

/**
 * La comanda (migration 0030). Open to owner AND employee alike, unlike the
 * owner-only menu CRUD: the person carrying the platos is who adds them.
 *
 * The server prices every settlement. Every call here sends what the counter
 * quoted and lets the server re-add its own rows; a disagreement comes back as
 * 409 `total_mismatch`, and the recovery is to read the tab again and re-quote
 * (the same triangle Cobrar runs).
 */

export function listOpenTabs(): Promise<{ data: TabRecord[] }> {
  return apiGet('/v1/business/tabs', TabListResponseSchema);
}

export function getTab(tabId: string): Promise<TabRecord> {
  return apiGet(`/v1/business/tabs/${encodeURIComponent(tabId)}`, TabResponseSchema).then(
    (result) => result.data,
  );
}

export function openTab(tableId: string, idempotencyKey: string): Promise<TabRecord> {
  return apiPost('/v1/business/tabs', TabResponseSchema, { tableId }, idempotencyKey).then(
    (result) => result.data,
  );
}

/** A menu reference OR a typed-in line; never both, never neither. */
export type AddTabItemInput =
  | { productId: string; quantity?: number; guestSlot?: number | null }
  | { name: string; unitPriceCents: number; quantity?: number; guestSlot?: number | null };

export function addTabItem(
  tabId: string,
  input: AddTabItemInput,
  idempotencyKey: string,
): Promise<TabRecord> {
  return apiPost(
    `/v1/business/tabs/${encodeURIComponent(tabId)}/items`,
    TabResponseSchema,
    input,
    idempotencyKey,
  ).then((result) => result.data);
}

/** How many, and for whom. The price never moves: that is what a snapshot is. */
export type PatchTabItemInput = {
  quantity?: number;
  guestSlot?: number | null;
  /** The version of the line the counter read; a stale one comes back 409 version_conflict with the line. */
  expectedVersion?: number;
};

export function patchTabItem(
  tabId: string,
  itemId: string,
  input: PatchTabItemInput,
  idempotencyKey: string,
): Promise<TabRecord> {
  return apiPatch(
    `/v1/business/tabs/${encodeURIComponent(tabId)}/items/${encodeURIComponent(itemId)}`,
    TabResponseSchema,
    input,
    idempotencyKey,
  ).then((result) => result.data);
}

export function removeTabItem(
  tabId: string,
  itemId: string,
  idempotencyKey: string,
): Promise<TabRecord> {
  return apiDelete(
    `/v1/business/tabs/${encodeURIComponent(tabId)}/items/${encodeURIComponent(itemId)}`,
    TabResponseSchema,
    idempotencyKey,
  ).then((result) => result.data);
}

/** Either one guest's lines, or one equal share of the whole tab. Never both. */
export type SettleTabTarget = { guestSlot: number | null } | { equalSplit: { parts: number; part: number } };

export function settleTab(
  tabId: string,
  input: SettleTabTarget & { expectedTotalCents: number; paymentMethod: ChargePaymentMethod },
  idempotencyKey: string,
): Promise<TabSettlement> {
  return apiPost(
    `/v1/business/tabs/${encodeURIComponent(tabId)}/settle`,
    TabSettlementResponseSchema,
    input,
    idempotencyKey,
  ).then((result) => result.data);
}
